import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Alert, CircularProgress,
} from '@mui/material';
import InvButton from '../InvButton';
import { getRfqClarifications, answerRfqClarification } from '../../api/rfq';
import { formatDateTime } from '../../utils';
import { IPD_COLORS } from '../../theme';

/**
 * Vendor questions from the public portal. Missing endpoints fail quietly —
 * older backends simply do not expose this route yet.
 */
export default function RfqClarificationsPanel({ rfqId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!rfqId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getRfqClarifications(rfqId);
      const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
      setRows(list);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => { load(); }, [load]);

  if (unavailable) {
    return (
      <Box sx={{ mt: 2, p: 2, borderRadius: '10px', border: `1px solid ${IPD_COLORS.border}` }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary, mb: 1 }}>
          Vendor questions
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          Question-and-answer is not on this backend yet. Vendors can still ask from the public portal once the clarifications API is live.
        </Typography>
      </Box>
    );
  }

  async function sendAnswer(row) {
    const answer = (drafts[row.id] || '').trim();
    if (answer.length < 4) return;
    setSavingId(row.id);
    setError(null);
    try {
      await answerRfqClarification(rfqId, row.id, {
        answer,
        broadcast: true,
      });
      await load();
    } catch (e) {
      setError(e.message || 'Could not send the answer');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: '10px', border: `1px solid ${IPD_COLORS.border}` }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary, mb: 1 }}>
        Vendor questions
      </Typography>
      {loading && <CircularProgress size={20} sx={{ color: IPD_COLORS.primary }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {!loading && rows.length === 0 && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          No questions from vendors on this RFQ.
        </Typography>
      )}
      {rows.map((row) => (
        <Box key={row.id} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${IPD_COLORS.border}` }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.question || row.q}</Typography>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {row.vendor_name || row.vendor_id || 'Vendor'} · {formatDateTime(row.at || row.created_at)}
            {row.status === 'PENDING' ? ' · Pending' : ' · Answered'}
          </Typography>
          {row.status === 'ANSWERED' && (
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              {row.answer || row.a}
              {row.broadcast ? ' · Shared with all invited vendors' : ''}
            </Typography>
          )}
          {row.status !== 'ANSWERED' && (
            <Box sx={{ mt: 1 }}>
              <TextField
                fullWidth size="small" multiline rows={2}
                label="Answer (shared with all invited vendors)"
                value={drafts[row.id] || ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [row.id]: e.target.value }))}
              />
              <InvButton
                size="small" tone="teal" sx={{ mt: 1 }}
                disabled={savingId === row.id || !(drafts[row.id] || '').trim()}
                onClick={() => sendAnswer(row)}
              >
                {savingId === row.id ? 'Sending…' : 'Send answer'}
              </InvButton>
            </Box>
          )}
        </Box>
      ))}
      <InvButton size="small" variant="outlined" tone="teal" onClick={load}>Refresh</InvButton>
    </Box>
  );
}
